import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    withStyles,
} from '@material-ui/core';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { connect } from 'react-redux';
import moment from 'moment';
import XLSX from 'xlsx';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import { ROLENAME } from '../../utils/index';

const NAVY = '#1C375D';
const ORANGE = '#F47E46';
const SNAG_BADGE_COLORS = ['#4A90D9', '#9B59B6', '#E67E22', '#1ABC9C'];

const getSnagBadgeColor = (index) => SNAG_BADGE_COLORS[index % SNAG_BADGE_COLORS.length];

const REASON_OPTIONS = [
    'Hardware not received',
    'Damaged in site',
    'Missing from site',
    'Extra requirement',
];

const LOG_ACTION_LABELS = {
    snag_created: 'Snag created',
    item_routed_direct_outward: 'Routed for direct outward',
    item_routed_approval: 'Routed for approval',
    item_routed_not_in_bom: 'Routed as additional requirement',
    approval_stage_approved: 'Approved',
    approval_fulfilled: 'Finance approved & fulfilled',
    approval_rejected: 'Rejected',
    item_outwarded: 'Outwarded',
    reason_updated: 'Reason updated',
};

const getLogActionLabel = (action = '') => LOG_ACTION_LABELS[action] || action || '—';

const formatLogDetails = (log = {}) => {
    const details = log.details || {};
    const parts = [];

    if (details.pieceName) parts.push(details.pieceName);
    if (log.stage) parts.push(getApprovalStageLabel(log.stage));
    if (details.nextStage) parts.push(`Next: ${getApprovalStageLabel(details.nextStage)}`);
    if (details.previousReason && details.newReason) {
        parts.push(`${details.previousReason} → ${details.newReason}`);
    }
    if (details.qty != null) parts.push(`Qty ${details.qty}`);
    if (details.modeOfTransport) parts.push(details.modeOfTransport);
    if (details.mtr?.orderId) parts.push(`MTR ${details.mtr.orderId}`);
    if (details.decision) parts.push(details.decision);

    return parts.length ? parts.join(' · ') : '—';
};

const ITEM_STATUS_READY_OUTWARD = 'Ready for Outward';

const FILTER_ALL = 'all';
const ASSIGNEE_FILTER_ME = 'me';

const ROWS_PER_PAGE_ALL = 'all';
const ROWS_PER_PAGE_OPTIONS = [100, 200, 500, ROWS_PER_PAGE_ALL];
const DEFAULT_ROWS_PER_PAGE = 100;

const STATUS_PENDING = 'Pending';
const STATUS_APPROVED = 'Approved';
const STATUS_OUTWARDED = 'Outwarded';
const STATUS_REJECTED = 'Rejected';

/** Raju's list is dispatch only. Ready for Outward already shows the approved lines waiting on him. */
const RAJU_STATUS_OPTIONS = [ITEM_STATUS_READY_OUTWARD, STATUS_OUTWARDED];

const DEFAULT_STATUS_OPTIONS = [
    STATUS_PENDING,
    STATUS_APPROVED,
    ITEM_STATUS_READY_OUTWARD,
    STATUS_OUTWARDED,
    STATUS_REJECTED,
];

/**
 * Approved spans both ready-to-dispatch and already-dispatched lines. Scoped to a
 * person it counts the lines they approved, which by then have moved on to the next
 * stage — so it cannot be answered from who currently holds the line.
 */
const getProjectStatusCount = (row = {}, status, assigneeId) => {
    if (assigneeId) {
        const own = (row.assignees || []).find((assignee) => String(assignee.id) === assigneeId);
        if (!own) return 0;
        if (status === STATUS_APPROVED) return own.approved ?? 0;
        if (status === ITEM_STATUS_READY_OUTWARD) return own.readyForOutward ?? 0;
        if (status === STATUS_OUTWARDED) return own.outwarded ?? 0;
        if (status === STATUS_REJECTED) return own.rejected ?? 0;
        return own.pendingApproval ?? 0;
    }

    if (status === STATUS_APPROVED) return row.approvedItems ?? 0;
    if (status === ITEM_STATUS_READY_OUTWARD) return row.readyForOutwardItems ?? 0;
    if (status === STATUS_OUTWARDED) return row.outwardedItems ?? 0;
    if (status === STATUS_REJECTED) return row.rejectedItems ?? 0;
    return row.pendingApprovalItems ?? 0;
};

const itemMatchesStatus = (item = {}, status) => {
    if (status === STATUS_OUTWARDED) return isOutwardedItem(item);
    if (status === STATUS_REJECTED) return isRejectedItem(item);
    if (status === ITEM_STATUS_READY_OUTWARD) {
        return item.status === ITEM_STATUS_READY_OUTWARD && !item.alreadyMtrOutwarded;
    }
    if (status === STATUS_APPROVED) {
        return item.status === ITEM_STATUS_READY_OUTWARD || isOutwardedItem(item);
    }
    return isPendingApproval(item);
};

const ITEM_ASSIGNEE_SNAPSHOT_FIELDS = [
    'assignedToCityOpsManager',
    'assignedToSeniorOperationManager',
    'assignedToCentralDirector',
    'assignedToFinanceManager',
    'assignedToProductionManager',
    'assignedToDefaultAssignee',
    'assignedToCreator',
];

/** Matches the current holder or any stage the line has already passed through. */
const itemMatchesAssignee = (item = {}, snag = {}, assigneeId) => {
    if (!assigneeId) return true;
    const target = String(assigneeId);
    const holder = String(item?.assignTo || snag?.assignToId || snag?.assignTo || '');
    if (holder === target) return true;
    return ITEM_ASSIGNEE_SNAPSHOT_FIELDS.some((field) => String(item?.[field]?.id || '') === target);
};

const getProjectStatusStyle = (status = '') => {
    if (status === STATUS_OUTWARDED) {
        return { backgroundColor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' };
    }
    if (status === STATUS_REJECTED) {
        return { backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };
    }
    if (status === STATUS_APPROVED || status === ITEM_STATUS_READY_OUTWARD) {
        return { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' };
    }
    return { backgroundColor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' };
};

/**
 * With a status selected the chip reports that status, since every listed row matched
 * it. Otherwise, scoped to one person it reports their own queue — so Raju sees
 * "Ready for Outward" on lines waiting to be dispatched rather than a generic "Pending".
 */
const getRowStatusLabel = (row = {}, assigneeId, statusFilter = FILTER_ALL) => {
    if (statusFilter !== FILTER_ALL) return statusFilter;
    if (assigneeId) {
        const own = (row.assignees || []).find((assignee) => String(assignee.id) === assigneeId);
        if (own?.pendingApproval > 0) return STATUS_PENDING;
        if (own?.readyForOutward > 0) return ITEM_STATUS_READY_OUTWARD;
        if (own?.outwarded > 0) return STATUS_OUTWARDED;
        if (own?.rejected > 0) return STATUS_REJECTED;
    }
    return row.status || STATUS_PENDING;
};

const getReasonChipStyle = (reason = '') => {
    const normalized = String(reason).toLowerCase();
    if (normalized.includes('damage')) {
        return { backgroundColor: '#fdecea', color: '#c0392b' };
    }
    if (normalized.includes('extra')) {
        return { backgroundColor: '#eaf4fb', color: '#2471a3' };
    }
    if (normalized.includes('missing')) {
        return { backgroundColor: '#eef2ff', color: '#4f46e5' };
    }
    return { backgroundColor: '#f3f4f6', color: '#4b5563' };
};

const DEFAULT_SNAG_ASSIGNEE_EMAIL = 'rajukumarsah@decorpot.com';

const normalizeEmail = (email = '') => String(email).trim().toLowerCase();

const isRajulUser = (userDetails = {}) =>
    normalizeEmail(userDetails?.email) === normalizeEmail(DEFAULT_SNAG_ASSIGNEE_EMAIL);

const isPendingApproval = (item = {}) => item.status === 'Pending Approval';

const isOutwardedItem = (item = {}) =>
    item.status === 'Outwarded' || !!item.alreadyMtrOutwarded;

const isRejectedItem = (item = {}) => item.status === 'Rejected';

const isAssignedToUser = (snag = {}, item = {}, userDetails = {}) => {
    const userId = String(userDetails?._id || userDetails?.id || '');
    const assigneeId = String(item?.assignTo || snag?.assignToId || snag?.assignTo || '');
    return !!(userId && assigneeId && userId === assigneeId);
};

const canEditReason = (item = {}, snag = {}, userDetails = {}) =>
    !isOutwardedItem(item)
    && !isRejectedItem(item)
    && isAssignedToUser(snag, item, userDetails);

const getItemStatusMeta = (item = {}) => {
    if (isOutwardedItem(item)) {
        return { label: 'Outwarded', color: '#047857', bg: '#ecfdf5', border: '#a7f3d0' };
    }
    if (isRejectedItem(item)) {
        return { label: 'Rejected', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' };
    }
    if (isPendingApproval(item)) {
        return { label: 'Pending Approval', color: '#b45309', bg: '#fffbeb', border: '#fde68a' };
    }
    if (item.status === 'Ready for Outward' && !item.alreadyMtrOutwarded) {
        return { label: 'Ready for Outward', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' };
    }
    return {
        label: item.status || 'Open',
        color: '#4b5563',
        bg: '#f3f4f6',
        border: '#e5e7eb',
    };
};

/**
 * Under the Approved filter a cleared line reads "Approved" rather than naming the
 * queue it moved on to, which is what the approver who signed it off expects to see.
 */
const getDisplayItemStatusMeta = (item = {}, statusFilter = FILTER_ALL) => {
    if (statusFilter === STATUS_APPROVED && itemMatchesStatus(item, STATUS_APPROVED)) {
        return { label: STATUS_APPROVED, color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' };
    }
    return getItemStatusMeta(item);
};

const getItemDisplayName = (item = {}) =>
    String(item.pieceName || item.description || item.sheetName || '').trim() || '—';

const getItemAssignedToName = (item = {}, snag = {}) =>
    item.assignedToDefaultAssignee?.name
    || item.assignedToFinanceManager?.name
    || item.assignedToCentralDirector?.name
    || item.assignedToCityOpsManager?.name
    || item.assignedToSeniorOperationManager?.name
    || snag.assignedTo
    || '—';

const getItemStatusDetails = (item = {}) => {
    const details = [];

    if (item.decision) details.push({ label: 'Decision', value: item.decision });
    if (item.approvedBy?.name) details.push({ label: 'Approved by', value: item.approvedBy.name });
    if (item.rejectedBy?.name) details.push({ label: 'Rejected by', value: item.rejectedBy.name });
    if (isPendingApproval(item) && item.approvalStage) {
        details.push({
            label: 'Awaiting',
            value: `${getApprovalStageLabel(item.approvalStage)} approval`,
        });
    }
    if (item.hardwareBom?.updatedQuantity != null) {
        details.push({ label: 'BOM', value: getBomChangeLabel(item.hardwareBom) });
    }
    if (item.mtr?.orderId) {
        const mtrQty = item.mtr.quantity ? ` (${item.mtr.quantity} qty)` : '';
        details.push({ label: 'MTR', value: `${item.mtr.orderId}${mtrQty}` });
    }
    if (item.alreadyMtrOutwarded || item.outwardViaExistingMtr) {
        const qty = item.mtrOutwardedQuantity ?? item.qty ?? '—';
        details.push({ label: 'Outwarded', value: `${qty} via MTR` });
    } else if (isOutwardedItem(item) && item.outward?.quantity) {
        details.push({ label: 'Outwarded', value: `${item.outward.quantity} from factory` });
    }
    if (isOutwardedItem(item) && item.outward?.modeOfTransport) {
        details.push({ label: 'Transport', value: item.outward.modeOfTransport });
    }
    if (isOutwardedItem(item) && item.outward?.dispatchDate) {
        details.push({ label: 'Dispatch', value: formatDate(item.outward.dispatchDate) });
    }
    return details;
};

const getBomChangeLabel = (hardwareBom = {}) => {
    if (hardwareBom.action === 'incremented') {
        return `BOM ${hardwareBom.previousQuantity} → ${hardwareBom.updatedQuantity}`;
    }
    if (hardwareBom.action === 'unchanged') {
        return `BOM unchanged (${hardwareBom.updatedQuantity})`;
    }
    return `BOM qty ${hardwareBom.updatedQuantity}`;
};

const getApprovalStageLabel = (stage = '') => {
    if (stage === 'som') return 'Senior Operation Manager';
    if (stage === 'central_director') return 'Central Director';
    if (stage === 'finance') return 'Finance Manager';
    return '';
};

const isAdminUser = (userDetails = {}) =>
    userDetails?.roles?.some((role) => role.name === ROLENAME.Admin);

/** DB role is stored as "Centeral Assistant Director". Match the label variants too. */
const isCenterAssistantDirector = (userDetails = {}) =>
    userDetails?.roles?.some((role) => {
        const name = String(role?.name || '').trim().toLowerCase();
        return name === 'centeral assistant director'
            || name === 'center assistant director'
            || name === 'central assistant director';
    });

const isCityOperationManager = (userDetails = {}) =>
    userDetails?.roles?.some((role) => {
        const name = String(role?.name || '').trim().toLowerCase();
        return name === 'city operation manager';
    });

const isFinanceManagerUser = (userDetails = {}) =>
    userDetails?.roles?.some((role) => role.name === ROLENAME.FinanceManager);

/**
 * Admin, Center Assistant Director, City Operation Manager, and Finance Manager can
 * filter every project/status when "All Assignees" is selected (approve/outward still
 * only on lines assigned to the logged-in user unless Admin).
 */
const canBrowseAllHardwareSnags = (userDetails = {}) =>
    isAdminUser(userDetails)
    || isCenterAssistantDirector(userDetails)
    || isCityOperationManager(userDetails)
    || isFinanceManagerUser(userDetails);

const canActOnSnagItem = (snag = {}, item = {}, userDetails = {}) => {
    if (isAdminUser(userDetails)) return true;

    const userId = String(userDetails?._id || userDetails?.id || '');
    const assigneeId = String(item?.assignTo || snag?.assignToId || snag?.assignTo || '');
    return !!(userId && assigneeId && userId === assigneeId);
};

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: NAVY,
        color: '#FFF2EB',
        fontWeight: 600,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        border: '1px solid #eee5e5',
        padding: '10px 12px',
        fontSize: 13,
    },
    body: {
        fontSize: 13,
        color: NAVY,
        textAlign: 'center',
        border: '1px solid #eee5e5',
        padding: '8px 12px',
        backgroundColor: '#fff',
    },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(2) },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(2),
    },
    title: {
        color: NAVY,
        fontWeight: 700,
        margin: 0,
    },
    search: { minWidth: 280 },
    filterBar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(1.5),
    },
    filterControl: { minWidth: 190 },
    filterSpacer: { flexGrow: 1 },
    resultCount: {
        fontSize: 12,
        fontWeight: 600,
        color: '#5f6b7a',
    },
    paginationBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        marginTop: theme.spacing(1.5),
    },
    filterBadge: {
        backgroundColor: '#fff4eb',
        color: '#c45a1f',
        border: '1px solid #ffd4b8',
        fontWeight: 600,
        fontSize: 11,
        padding: '4px 10px',
        borderRadius: 999,
    },
    rowStatusChip: {
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
    actionBtn: {
        backgroundColor: ORANGE,
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#E0663D',
        },
    },
    tableContainer: {
        marginTop: theme.spacing(1),
        overflowX: 'auto',
    },
    clickableCount: {
        color: NAVY,
        fontWeight: 700,
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:hover': {
            color: ORANGE,
        },
    },
    disabledCount: {
        color: '#9e9e9e',
    },
    dialogTitle: {
        backgroundColor: '#fff',
        color: NAVY,
        padding: '20px 24px 0',
    },
    dialogTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    dialogTitleText: {
        fontWeight: 700,
        fontSize: 22,
        color: NAVY,
    },
    projectBadge: {
        backgroundColor: '#eef2f7',
        color: NAVY,
        fontWeight: 600,
        fontSize: 12,
        padding: '4px 10px',
        borderRadius: 999,
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        margin: '16px 0 8px',
    },
    summaryCard: {
        border: '1px solid #e8edf3',
        borderRadius: 10,
        padding: '12px 14px',
        backgroundColor: '#fafbfd',
    },
    summaryLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        color: '#7b8794',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 4,
    },
    summaryValue: {
        fontSize: 14,
        fontWeight: 600,
        color: NAVY,
    },
    dialogContent: {
        padding: '8px 24px 24px',
        backgroundColor: '#f6f8fb',
        maxHeight: '72vh',
        overflowY: 'auto',
    },
    snagAccordion: {
        marginBottom: theme.spacing(1.5),
        border: '1px solid #e8edf3',
        borderRadius: '12px !important',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(28, 55, 93, 0.06)',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: `0 0 ${theme.spacing(1.5)}px 0` },
    },
    snagSummary: {
        padding: theme.spacing(1.25, 2),
        minHeight: 52,
        '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            margin: '8px 0',
        },
    },
    snagSummaryInner: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        width: '100%',
        paddingRight: 8,
    },
    snagBadge: {
        color: '#fff',
        fontWeight: 700,
        fontSize: 12,
        padding: '4px 10px',
        borderRadius: 999,
        lineHeight: 1.2,
        flexShrink: 0,
    },
    snagMeta: {
        fontSize: 12,
        color: '#5f6b7a',
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
    },
    snagMetaDivider: {
        color: '#d1d5db',
        margin: '0 2px',
    },
    snagDetails: {
        padding: theme.spacing(0, 2, 2),
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    itemCard: {
        border: '1px solid #eef2f6',
        borderRadius: 10,
        padding: theme.spacing(1.5, 1.75),
        backgroundColor: '#fafbfd',
    },
    itemCardTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 10,
    },
    itemName: {
        fontSize: 14,
        fontWeight: 700,
        color: NAVY,
        lineHeight: 1.4,
    },
    itemBadges: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 6,
    },
    statusChip: {
        height: 24,
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 6,
    },
    qtyGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(70px, 1fr))',
        gap: 8,
        marginBottom: 12,
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
        },
    },
    qtyBox: {
        backgroundColor: '#fff',
        border: '1px solid #e8edf3',
        borderRadius: 8,
        padding: '8px 10px',
        textAlign: 'center',
    },
    qtyLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 2,
    },
    qtyValue: {
        fontSize: 15,
        fontWeight: 700,
        color: NAVY,
    },
    itemCardBottom: {
        display: 'grid',
        gridTemplateColumns: 'minmax(160px, 1fr) minmax(120px, 1fr) auto',
        gap: 12,
        alignItems: 'start',
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
        },
    },
    fieldLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 4,
    },
    remarkText: {
        fontSize: 13,
        color: '#374151',
        lineHeight: 1.45,
        wordBreak: 'break-word',
    },
    statusDetailList: {
        marginTop: 10,
        paddingTop: 10,
        borderTop: '1px dashed #e5e7eb',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
    },
    detailChip: {
        height: 'auto',
        fontSize: 11,
        fontWeight: 500,
        borderRadius: 6,
        padding: '4px 0',
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        '& .MuiChip-label': {
            whiteSpace: 'normal',
            lineHeight: 1.35,
            padding: '2px 8px',
        },
    },
    viewOnlyNote: {
        fontSize: 11,
        color: '#9ca3af',
        fontStyle: 'italic',
        marginTop: 4,
    },
    reasonChip: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    actionCell: {
        minWidth: 220,
        verticalAlign: 'top',
    },
    rowActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'stretch',
    },
    actionButton: {
        borderRadius: 8,
        padding: '9px 12px',
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'none',
        lineHeight: 1.35,
        boxShadow: 'none',
        justifyContent: 'flex-start',
        '& .MuiButton-startIcon': {
            marginRight: 8,
            marginLeft: 0,
        },
        '&:hover': {
            boxShadow: 'none',
        },
    },
    presentBtn: {
        backgroundColor: '#edf7ed',
        color: '#2e6b2e',
        border: '1px solid #c8e6c9',
        '&:hover': {
            backgroundColor: '#dff0df',
        },
    },
    presentBtnActive: {
        backgroundColor: '#edf7ed',
        color: '#2e6b2e',
        border: '1px solid #a5d6a7',
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: '#edf7ed',
            color: '#2e6b2e',
            border: '1px solid #a5d6a7',
        },
    },
    presentBtnMuted: {
        backgroundColor: '#f5f5f5',
        color: '#bdbdbd',
        border: '1px solid #e0e0e0',
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: '#f5f5f5',
            color: '#bdbdbd',
            border: '1px solid #e0e0e0',
        },
    },
    notPresentBtn: {
        backgroundColor: '#fff4eb',
        color: '#c45a1f',
        border: '1px solid #ffd4b8',
        '&:hover': {
            backgroundColor: '#ffe8d6',
        },
    },
    notPresentBtnActive: {
        backgroundColor: '#fff4eb',
        color: '#c45a1f',
        border: '1px solid #ffb885',
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: '#fff4eb',
            color: '#c45a1f',
            border: '1px solid #ffb885',
        },
    },
    notPresentBtnMuted: {
        backgroundColor: '#f5f5f5',
        color: '#bdbdbd',
        border: '1px solid #e0e0e0',
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: '#f5f5f5',
            color: '#bdbdbd',
            border: '1px solid #e0e0e0',
        },
    },
    itemStatus: {
        fontSize: 11,
        fontWeight: 500,
        color: '#6b7280',
        marginTop: 2,
        lineHeight: 1.45,
        textAlign: 'left',
    },
    closeBtn: {
        borderColor: NAVY,
        color: NAVY,
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '6px 18px',
    },
    approveBtn: {
        backgroundColor: NAVY,
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 8,
        padding: '9px 12px',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#152a45',
            boxShadow: 'none',
        },
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: '#cfd8e3',
            color: '#fff',
        },
    },
    rejectBtn: {
        backgroundColor: '#fff',
        color: '#c0392b',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 8,
        padding: '9px 12px',
        border: '1px solid #f5c6cb',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#fdecea',
            boxShadow: 'none',
        },
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: '#f5f5f5',
            color: '#bdbdbd',
            border: '1px solid #e0e0e0',
        },
    },
    outwardBtn: {
        backgroundColor: ORANGE,
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 8,
        padding: '9px 12px',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#E0663D',
            boxShadow: 'none',
        },
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: '#cfd8e3',
            color: '#fff',
        },
    },
    chainActions: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
    },
    infoBtn: {
        marginLeft: 'auto',
        color: NAVY,
        padding: 6,
        '&:hover': {
            backgroundColor: 'rgba(28, 55, 93, 0.08)',
        },
    },
    reasonSelect: {
        fontSize: 12,
        fontWeight: 600,
        width: '100%',
        backgroundColor: '#fff',
        borderRadius: 8,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e5e7eb',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: NAVY,
        },
        '& .MuiSelect-select': {
            padding: '8px 12px',
        },
    },
    logTimeline: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        marginTop: 8,
    },
    logEntry: {
        border: '1px solid #e8edf3',
        borderRadius: 10,
        padding: '12px 14px',
        backgroundColor: '#fff',
    },
    logEntryHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 6,
    },
    logAction: {
        fontSize: 13,
        fontWeight: 700,
        color: NAVY,
    },
    logMeta: {
        fontSize: 12,
        color: '#6b7280',
    },
    logDetailText: {
        fontSize: 12,
        color: '#4b5563',
        lineHeight: 1.45,
    },
    logTable: {
        marginTop: 8,
    },
    logEmpty: {
        padding: theme.spacing(3),
        textAlign: 'center',
        color: '#5f6b7a',
    },
}));

const formatDate = (value) => {
    if (!value) return '—';
    const formatted = moment(value);
    return formatted.isValid() ? formatted.format('DD-MM-YYYY HH:mm') : '—';
};

const getMinDispatchDate = () => moment().format('YYYY-MM-DD');

const isDispatchDateAllowed = (value) => {
    const selected = moment(value, 'YYYY-MM-DD', true);
    if (!selected.isValid()) return false;
    return !selected.isBefore(moment(), 'day');
};

const getProjectItemStats = (snags = []) => {
    const stats = { pending: 0, ready: 0, outwarded: 0, rejected: 0 };
    snags.forEach((snag) => {
        (snag.items || []).forEach((item) => {
            if (isOutwardedItem(item)) stats.outwarded += 1;
            else if (isRejectedItem(item)) stats.rejected += 1;
            else if (isPendingApproval(item)) stats.pending += 1;
            else if (item.status === 'Ready for Outward') stats.ready += 1;
        });
    });
    return stats;
};

const HardwareSnags = (props) => {
    const classes = useStyles();
    const { userDetails = {} } = props;
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const [cityFilter, setCityFilter] = useState(FILTER_ALL);
    const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
    const [assigneeFilter, setAssigneeFilter] = useState(FILTER_ALL);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [page, setPage] = useState(0);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [outwardDialogOpen, setOutwardDialogOpen] = useState(false);
    const [outwardTarget, setOutwardTarget] = useState(null);
    const [modeOfTransport, setModeOfTransport] = useState('');
    const [dispatchDate, setDispatchDate] = useState('');
    const [logDialogOpen, setLogDialogOpen] = useState(false);
    const [logTarget, setLogTarget] = useState(null);
    const [snagLogs, setSnagLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [reasonUpdatingId, setReasonUpdatingId] = useState(null);
    const [details, setDetails] = useState({
        projectNo: '',
        customerName: '',
        city: '',
        totalSnags: 0,
        snags: [],
    });

    const getApproverPayload = () => ({
        approvedBy: {
            id: userDetails?._id || userDetails?.id || null,
            name: userDetails?.name || '',
            email: userDetails?.email || '',
        },
    });

    const fetchProjects = useCallback(async (searchText = '') => {
        props.setLoading(true);
        try {
            const res = await HttpService.getHardwareSnagProjects({
                search: searchText || undefined,
            });
            setRows(res.data?.projects || []);
        } catch (error) {
            setRows([]);
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to load hardware snags',
                'error'
            );
        }
        props.setLoading(false);
    }, [props]);

    const loadProjectDetails = useCallback(async (projectNo) => {
        const res = await HttpService.getHardwareSnagProjectDetails(projectNo);
        const data = res.data || {};
        setDetails({
            projectNo: data.projectNo || projectNo,
            customerName: data.customerName || '—',
            city: data.city || '—',
            totalSnags: data.totalSnags || 0,
            snags: data.snags || [],
        });
        return data;
    }, []);

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        setActiveSearch(search.trim());
        fetchProjects(search.trim());
    };

    const handleClear = () => {
        setSearch('');
        setActiveSearch('');
        setCityFilter(FILTER_ALL);
        setStatusFilter(FILTER_ALL);
        setAssigneeFilter(FILTER_ALL);
        setPage(0);
        fetchProjects('');
    };

    const canBrowseAll = canBrowseAllHardwareSnags(userDetails);
    const currentUserId = String(userDetails?._id || userDetails?.id || '');

    const cityOptions = useMemo(() => {
        const cities = new Set();
        rows.forEach((row) => {
            const city = String(row.city || '').trim();
            if (city && city !== '—') cities.add(city);
        });
        return [...cities].sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const assigneeOptions = useMemo(() => {
        const byId = new Map();
        rows.forEach((row) => {
            (row.assignees || []).forEach((assignee) => {
                if (!assignee?.id || byId.has(assignee.id)) return;
                byId.set(assignee.id, assignee);
            });
        });
        return [...byId.values()].sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''))
        );
    }, [rows]);

    const selectedAssigneeId = assigneeFilter === FILTER_ALL
        ? ''
        : (assigneeFilter === ASSIGNEE_FILTER_ME ? currentUserId : assigneeFilter);

    /**
     * Every status is somebody's queue, so a scoped user picking one sees only their own
     * lines. Browse-all roles keep the global view when "All Assignees" is selected.
     */
    const filterAssigneeId = selectedAssigneeId
        || (!canBrowseAll && statusFilter !== FILTER_ALL ? currentUserId : '');

    const statusOptions = useMemo(() => {
        const selected = assigneeOptions.find((assignee) => String(assignee.id) === selectedAssigneeId);
        const viewingRaju = isRajulUser(userDetails)
            || (selected && normalizeEmail(selected.email) === normalizeEmail(DEFAULT_SNAG_ASSIGNEE_EMAIL));
        return viewingRaju ? RAJU_STATUS_OPTIONS : DEFAULT_STATUS_OPTIONS;
    }, [assigneeOptions, selectedAssigneeId, userDetails]);

    useEffect(() => {
        if (statusFilter !== FILTER_ALL && !statusOptions.includes(statusFilter)) {
            setStatusFilter(FILTER_ALL);
            setPage(0);
        }
    }, [statusOptions, statusFilter]);

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            if (cityFilter !== FILTER_ALL && String(row.city || '') !== cityFilter) {
                return false;
            }
            // A scoped status count already encodes "this person's lines", so the
            // assignee check below is only needed when no status is selected.
            if (statusFilter !== FILTER_ALL) {
                return getProjectStatusCount(row, statusFilter, filterAssigneeId) > 0;
            }
            if (!filterAssigneeId) return true;
            return (row.assignees || []).some((assignee) =>
                String(assignee.id) === filterAssigneeId && assignee.open);
        });
    }, [rows, cityFilter, statusFilter, filterAssigneeId]);

    const isDetailsFiltered = statusFilter !== FILTER_ALL || !!filterAssigneeId;

    const filterAssigneeName = filterAssigneeId === currentUserId
        ? 'me'
        : (assigneeOptions.find((assignee) => assignee.id === filterAssigneeId)?.name
            || 'the selected user');

    const visibleSnags = useMemo(() => {
        const numbered = details.snags.map((snag, snagIndex) => ({ ...snag, snagIndex }));
        if (!isDetailsFiltered) return numbered;

        return numbered
            .map((snag) => ({
                ...snag,
                items: (snag.items || []).filter((item) =>
                    (statusFilter === FILTER_ALL || itemMatchesStatus(item, statusFilter))
                    && itemMatchesAssignee(item, snag, filterAssigneeId)),
            }))
            .filter((snag) => snag.items.length);
    }, [details.snags, isDetailsFiltered, statusFilter, filterAssigneeId]);

    const pageSize = rowsPerPage === ROWS_PER_PAGE_ALL ? filteredRows.length : rowsPerPage;
    const pageCount = pageSize > 0 ? Math.ceil(filteredRows.length / pageSize) : 1;
    const currentPage = Math.min(page, Math.max(pageCount - 1, 0));
    const pagedRows = rowsPerPage === ROWS_PER_PAGE_ALL
        ? filteredRows
        : filteredRows.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setPage(0);
    };

    const handleDownloadCsv = async () => {
        if (!filteredRows.length) {
            props.setSnackInfo('No rows to download', 'warning');
            return;
        }

        props.setLoading(true);
        try {
            const summaryRows = filteredRows.map((row, index) => ({
                SN: index + 1,
                'Project No': row.projectNo || '',
                Customer: row.customerName || '',
                'Assigned To': row.assignedTo || '',
                City: row.city || '',
                Status: getRowStatusLabel(row, filterAssigneeId, statusFilter),
                'Pending Approval Items': row.pendingApprovalItems ?? 0,
                'Ready for Outward Items': row.readyForOutwardItems ?? 0,
                'Outwarded Items': row.outwardedItems ?? 0,
                'Rejected Items': row.rejectedItems ?? 0,
                'Total No of Snag': row.totalSnags ?? 0,
                'Last Updated Date': formatDate(row.lastUpdatedDate),
            }));

            const exportRes = await HttpService.exportHardwareSnagProjectItems(
                filteredRows.map((row) => row.projectNo)
            );
            const projectsByNo = exportRes.data?.projects || {};

            const itemRows = [];
            filteredRows.forEach((row) => {
                const data = projectsByNo[row.projectNo];
                if (!data?.snags?.length) return;

                data.snags.forEach((snag, snagIndex) => {
                    (snag.items || []).forEach((item, itemIndex) => {
                        if (statusFilter !== FILTER_ALL && !itemMatchesStatus(item, statusFilter)) {
                            return;
                        }
                        if (!itemMatchesAssignee(item, snag, filterAssigneeId)) {
                            return;
                        }

                        itemRows.push({
                            SN: itemRows.length + 1,
                            'Project No': row.projectNo || data.projectNo || '',
                            Customer: row.customerName || data.customerName || '',
                            City: row.city || data.city || '',
                            'Snag No': snagIndex + 1,
                            'Item No': item.sn ?? itemIndex + 1,
                            'Item Name': getItemDisplayName(item),
                            'Sheet Name': item.sheetName || '',
                            Qty: item.qty ?? '',
                            Reason: item.reason || '',
                            'Item Status': getDisplayItemStatusMeta(item, statusFilter).label,
                            'Assigned To': getItemAssignedToName(item, snag),
                            'Project Assigned To': snag.assignedTo || row.assignedTo || '',
                        });
                    });
                });
            });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
                workbook,
                XLSX.utils.json_to_sheet(summaryRows),
                'Project Summary'
            );

            const itemSheetHeaders = [
                'SN',
                'Project No',
                'Customer',
                'City',
                'Snag No',
                'Item No',
                'Item Name',
                'Sheet Name',
                'Qty',
                'Reason',
                'Item Status',
                'Assigned To',
                'Project Assigned To',
            ];
            XLSX.utils.book_append_sheet(
                workbook,
                itemRows.length
                    ? XLSX.utils.json_to_sheet(itemRows)
                    : XLSX.utils.aoa_to_sheet([itemSheetHeaders]),
                'Item Details'
            );

            XLSX.writeFile(
                workbook,
                `hardware_snags_${moment().format('YYYYMMDD_HHmmss')}.xlsx`
            );
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to download export',
                'error'
            );
        }
        props.setLoading(false);
    };

    const openDetails = async (row) => {
        if (!row?.projectNo || !row?.totalSnags) return;
        props.setLoading(true);
        try {
            await loadProjectDetails(row.projectNo);
            setDetailsOpen(true);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to load snag details',
                'error'
            );
        }
        props.setLoading(false);
    };

    const openOutwardDialog = (snag, item) => {
        setOutwardTarget({ snag, item });
        setModeOfTransport('');
        setDispatchDate(moment().format('YYYY-MM-DD'));
        setOutwardDialogOpen(true);
    };

    const openLogDialog = async (snag, snagIndex) => {
        setLogTarget({ snag, snagIndex });
        setLogDialogOpen(true);
        setSnagLogs([]);
        setLogsLoading(true);
        try {
            const res = await HttpService.getHardwareSnagLogs(snag._id);
            setSnagLogs(res.data?.logs || []);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to load snag history',
                'error'
            );
        }
        setLogsLoading(false);
    };

    const closeLogDialog = () => {
        setLogDialogOpen(false);
        setLogTarget(null);
        setSnagLogs([]);
    };

    const handleReasonChange = async (snag, item, newReason) => {
        const itemKey = `${snag._id}-${item.itemId}`;
        if (!newReason || newReason === item.reason) return;

        setReasonUpdatingId(itemKey);
        try {
            await HttpService.updateHardwareSnagItemReason(snag._id, {
                itemId: item.itemId,
                reasonForChange: newReason,
            });
            props.setSnackInfo('Reason updated', 'success');
            await loadProjectDetails(details.projectNo);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to update reason',
                'error'
            );
        }
        setReasonUpdatingId(null);
    };

    const closeOutwardDialog = () => {
        setOutwardDialogOpen(false);
        setOutwardTarget(null);
        setModeOfTransport('');
        setDispatchDate('');
    };

    const handleOutward = async () => {
        const snag = outwardTarget?.snag;
        const item = outwardTarget?.item;
        if (!snag?._id || !item?.itemId || !item?.canOutward) return;

        const trimmedMode = modeOfTransport.trim();
        if (!trimmedMode) {
            props.setSnackInfo('Mode of Transport is required', 'error');
            return;
        }
        if (!dispatchDate) {
            props.setSnackInfo('Dispatch Date is required', 'error');
            return;
        }
        if (!isDispatchDateAllowed(dispatchDate)) {
            props.setSnackInfo('Dispatch date cannot be in the past', 'error');
            return;
        }

        const actionKey = `${snag._id}-${item.itemId}`;
        setActionLoadingId(actionKey);
        try {
            const res = await HttpService.outwardHardwareSnagItem(snag._id, {
                ...getApproverPayload(),
                itemId: item.itemId,
                modeOfTransport: trimmedMode,
                dispatchDate,
            });
            props.setSnackInfo(res.data?.message || 'Item outwarded successfully', 'success');
            closeOutwardDialog();
            await loadProjectDetails(details.projectNo);
            fetchProjects(activeSearch);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to outward item',
                'error'
            );
        }
        setActionLoadingId(null);
    };

    const handleApproveNotPresentInBom = async (snag, item) => {
        if (!snag?._id || !item?.itemId || !isPendingApproval(item)) return;
        const actionKey = `${snag._id}-${item.itemId}`;
        setActionLoadingId(actionKey);
        try {
            const res = await HttpService.approveHardwareSnagNotPresentInBom(snag._id, {
                ...getApproverPayload(),
                itemId: item.itemId,
            });
            const assignedName = res.data?.assignedTo?.name;
            const stageLabel = getApprovalStageLabel(res.data?.approvalStage);
            props.setSnackInfo(
                res.data?.message
                    || (assignedName
                        ? `Approved and assigned to ${assignedName}${stageLabel ? ` (${stageLabel})` : ''}`
                        : 'Not Present in BOM item approved'),
                'success'
            );
            await loadProjectDetails(details.projectNo);
            fetchProjects(activeSearch);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to approve item',
                'error'
            );
        }
        setActionLoadingId(null);
    };

    const handleRejectNotPresentInBom = async (snag, item) => {
        if (!snag?._id || !item?.itemId || !isPendingApproval(item)) return;
        const actionKey = `${snag._id}-${item.itemId}`;
        setActionLoadingId(actionKey);
        try {
            const res = await HttpService.rejectHardwareSnagNotPresentInBom(snag._id, {
                ...getApproverPayload(),
                itemId: item.itemId,
            });
            props.setSnackInfo(
                res.data?.message || 'Not Present in BOM item rejected',
                'success'
            );
            await loadProjectDetails(details.projectNo);
            fetchProjects(activeSearch);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || 'Failed to reject item',
                'error'
            );
        }
        setActionLoadingId(null);
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <h1 className={classes.title}>Hardware Snags</h1>
                <Box display="flex" alignItems="center" style={{ gap: 12 }} flexWrap="wrap">
                    <TextField
                        className={classes.search}
                        size="small"
                        variant="outlined"
                        label="Search project / customer"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSearch();
                        }}
                    />
                    <Button className={classes.actionBtn} variant="contained" onClick={handleSearch}>
                        Search
                    </Button>
                    <Button variant="outlined" onClick={handleClear}>
                        Clear
                    </Button>
                </Box>
            </div>

            <div className={classes.filterBar}>
                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                    <Select value={cityFilter} onChange={handleFilterChange(setCityFilter)}>
                        <MenuItem value={FILTER_ALL}>All Cities</MenuItem>
                        {cityOptions.map((city) => (
                            <MenuItem key={city} value={city}>{city}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                    <Select value={statusFilter} onChange={handleFilterChange(setStatusFilter)}>
                        <MenuItem value={FILTER_ALL}>All Statuses</MenuItem>
                        {statusOptions.map((status) => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.filterControl}>
                    <Select value={assigneeFilter} onChange={handleFilterChange(setAssigneeFilter)}>
                        <MenuItem value={FILTER_ALL}>All Assignees</MenuItem>
                        <MenuItem value={ASSIGNEE_FILTER_ME}>Assigned to Me</MenuItem>
                        {canBrowseAll ? assigneeOptions.map((assignee) => (
                            <MenuItem key={assignee.id} value={assignee.id}>
                                {assignee.name || assignee.email}
                                {assignee.stageLabel ? ` (${assignee.stageLabel})` : ''}
                            </MenuItem>
                        )) : null}
                    </Select>
                </FormControl>
                <span className={classes.filterSpacer} />
                <span className={classes.resultCount}>{filteredRows.length} project(s)</span>
                <Button variant="outlined" onClick={handleDownloadCsv}>
                    Download Excel
                </Button>
            </div>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>SN</StyledTableCell>
                            <StyledTableCell>Project No</StyledTableCell>
                            <StyledTableCell>Customer</StyledTableCell>
                            <StyledTableCell>Assigned To</StyledTableCell>
                            <StyledTableCell>City</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                            <StyledTableCell>Last Updated Date</StyledTableCell>
                            <StyledTableCell>Total No of Snag</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedRows.map((row, index) => (
                            <TableRow key={row.projectNo}>
                                <StyledTableCell>{currentPage * pageSize + index + 1}</StyledTableCell>
                                <StyledTableCell>{row.projectNo || '—'}</StyledTableCell>
                                <StyledTableCell>{row.customerName || '—'}</StyledTableCell>
                                <StyledTableCell>{row.assignedTo || '—'}</StyledTableCell>
                                <StyledTableCell>{row.city || '—'}</StyledTableCell>
                                <StyledTableCell>
                                    <span
                                        className={classes.rowStatusChip}
                                        style={getProjectStatusStyle(
                                            getRowStatusLabel(row, filterAssigneeId, statusFilter)
                                        )}
                                    >
                                        {getRowStatusLabel(row, filterAssigneeId, statusFilter)}
                                    </span>
                                </StyledTableCell>
                                <StyledTableCell>{formatDate(row.lastUpdatedDate)}</StyledTableCell>
                                <StyledTableCell>
                                    {row.totalSnags ? (
                                        <span
                                            className={classes.clickableCount}
                                            onClick={() => openDetails(row)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') openDetails(row);
                                            }}
                                        >
                                            {row.totalSnags}
                                        </span>
                                    ) : (
                                        <span className={classes.disabledCount}>0</span>
                                    )}
                                </StyledTableCell>
                            </TableRow>
                        ))}

                        {!pagedRows.length && (
                            <TableRow>
                                <StyledTableCell colSpan={8}>
                                    {activeSearch
                                        ? `No hardware snags found for "${activeSearch}".`
                                        : 'No hardware snags found.'}
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <div className={classes.paginationBar}>
                <span className={classes.resultCount}>Rows per page</span>
                <FormControl variant="outlined" size="small">
                    <Select
                        value={rowsPerPage}
                        onChange={(event) => {
                            setRowsPerPage(event.target.value);
                            setPage(0);
                        }}
                    >
                        {ROWS_PER_PAGE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option === ROWS_PER_PAGE_ALL ? 'All' : option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <span className={classes.resultCount}>
                    Page {pageCount ? currentPage + 1 : 0} of {pageCount}
                </span>
                <Button
                    variant="outlined"
                    size="small"
                    disabled={currentPage <= 0}
                    onClick={() => setPage(currentPage - 1)}
                >
                    Previous
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    disabled={currentPage >= pageCount - 1}
                    onClick={() => setPage(currentPage + 1)}
                >
                    Next
                </Button>
            </div>

            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth="xl"
                fullWidth
                scroll="paper"
            >
                <DialogTitle className={classes.dialogTitle} disableTypography>
                    <div className={classes.dialogTitleRow}>
                        <Typography className={classes.dialogTitleText}>Hardware Snag Details</Typography>
                        <span className={classes.projectBadge}>{details.projectNo}</span>
                        {isDetailsFiltered && (
                            <span className={classes.filterBadge}>
                                Showing {statusFilter === FILTER_ALL ? 'all' : statusFilter.toLowerCase()} items
                                {filterAssigneeId ? ` assigned to ${filterAssigneeName}` : ''}
                            </span>
                        )}
                    </div>
                    <div className={classes.summaryCards}>
                        <div className={classes.summaryCard}>
                            <div className={classes.summaryLabel}>
                                <PersonOutlineIcon style={{ fontSize: 14 }} />
                                Customer
                            </div>
                            <div className={classes.summaryValue}>
                                {details.customerName} | City: {details.city}
                            </div>
                        </div>
                        <div className={classes.summaryCard}>
                            <div className={classes.summaryLabel}>
                                <LocalOfferOutlinedIcon style={{ fontSize: 14 }} />
                                Total Snags
                            </div>
                            <div className={classes.summaryValue}>{details.totalSnags}</div>
                        </div>
                        <div className={classes.summaryCard}>
                            <div className={classes.summaryLabel}>
                                <EventOutlinedIcon style={{ fontSize: 14 }} />
                                Last Updated
                            </div>
                            <div className={classes.summaryValue}>
                                {formatDate(
                                    details.snags.reduce((latest, snag) => {
                                        const ts = moment(snag.updatedAt);
                                        if (!ts.isValid()) return latest;
                                        return !latest || ts.isAfter(latest) ? ts : latest;
                                    }, null)
                                )}
                            </div>
                        </div>
                        {(() => {
                            const stats = getProjectItemStats(details.snags);
                            const parts = [
                                stats.pending ? `${stats.pending} pending` : '',
                                stats.ready ? `${stats.ready} ready` : '',
                                stats.outwarded ? `${stats.outwarded} outwarded` : '',
                                stats.rejected ? `${stats.rejected} rejected` : '',
                            ].filter(Boolean);
                            if (!parts.length) return null;
                            return (
                                <div className={classes.summaryCard}>
                                    <div className={classes.summaryLabel}>
                                        <LocalOfferOutlinedIcon style={{ fontSize: 14 }} />
                                        Item Status
                                    </div>
                                    <div className={classes.summaryValue}>{parts.join(' · ')}</div>
                                </div>
                            );
                        })()}
                    </div>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {visibleSnags.map((snag, position) => {
                        const snagIndex = snag.snagIndex;
                        return (
                        <Accordion
                            key={snag._id || snagIndex}
                            className={classes.snagAccordion}
                            defaultExpanded={position === visibleSnags.length - 1}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                className={classes.snagSummary}
                            >
                                <div className={classes.snagSummaryInner}>
                                    <span
                                        className={classes.snagBadge}
                                        style={{ backgroundColor: getSnagBadgeColor(snagIndex) }}
                                    >
                                        Snag {snagIndex + 1}
                                    </span>
                                    <span className={classes.snagMeta}>
                                        <PersonOutlineIcon style={{ fontSize: 14 }} />
                                        {snag.raiseByName || '—'}
                                    </span>
                                    <span className={classes.snagMetaDivider}>·</span>
                                    <span className={classes.snagMeta}>
                                        <EventOutlinedIcon style={{ fontSize: 14 }} />
                                        {formatDate(snag.updatedAt)}
                                    </span>
                                    <Tooltip title="View approval & activity history">
                                        <IconButton
                                            className={classes.infoBtn}
                                            size="small"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                openLogDialog(snag, snagIndex);
                                            }}
                                        >
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails className={classes.snagDetails}>
                                {(snag.items || []).map((item, itemIndex) => {
                                    const pendingApproval = isPendingApproval(item);
                                    const actionKey = `${snag._id}-${item.itemId || itemIndex}`;
                                    const isBusy = actionLoadingId === actionKey;
                                    const canAct = canActOnSnagItem(snag, item, userDetails);
                                    const showOutwardButton = item.canOutward
                                        && isRajulUser(userDetails)
                                        && canAct
                                        && !isOutwardedItem(item);
                                    const showChainButtons = pendingApproval && canAct && !isRajulUser(userDetails);
                                    const statusMeta = getDisplayItemStatusMeta(item, statusFilter);
                                    const statusDetails = getItemStatusDetails(item);
                                    const itemAssignedTo = item.assignedToDefaultAssignee?.name
                                        || item.assignedToFinanceManager?.name
                                        || item.assignedToCentralDirector?.name
                                        || item.assignedToCityOpsManager?.name
                                        || item.assignedToSeniorOperationManager?.name
                                        || snag.assignedTo
                                        || '—';

                                    return (
                                        <div
                                            className={classes.itemCard}
                                            key={item.itemId || `${snag._id}-${itemIndex}`}
                                        >
                                            <div className={classes.itemCardTop}>
                                                <div>
                                                    <Typography className={classes.itemName}>
                                                        {item.sn ?? itemIndex + 1}. {item.pieceName || '—'}
                                                    </Typography>
                                                    <div className={classes.itemBadges}>
                                                        {item.isNotInBom && (
                                                            <Chip
                                                                size="small"
                                                                label="Additional Requirement"
                                                                className={classes.statusChip}
                                                                style={{
                                                                    backgroundColor: '#eef2ff',
                                                                    color: '#4f46e5',
                                                                    border: '1px solid #c7d2fe',
                                                                }}
                                                            />
                                                        )}
                                                        <Chip
                                                            size="small"
                                                            label={`Assigned to: ${itemAssignedTo}`}
                                                            className={classes.statusChip}
                                                            style={{
                                                                backgroundColor: '#eef6ff',
                                                                color: '#1d4ed8',
                                                                border: '1px solid #bfdbfe',
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <Chip
                                                    size="small"
                                                    label={statusMeta.label}
                                                    className={classes.statusChip}
                                                    style={{
                                                        backgroundColor: statusMeta.bg,
                                                        color: statusMeta.color,
                                                        border: `1px solid ${statusMeta.border}`,
                                                    }}
                                                />
                                            </div>

                                            <div className={classes.qtyGrid}>
                                                {[
                                                    { label: 'BOM', value: item.bomQty ?? item.hardwareBom?.updatedQuantity ?? '—' },
                                                    {
                                                        label: 'MTR Outwarded',
                                                        value: item.mtrOutwardedQuantity ?? item.mtrOutwardedQty ?? '—',
                                                    },
                                                    { label: 'Pending', value: item.pendingMtrQty ?? '—' },
                                                    { label: 'Requested', value: item.qty ?? '—' },
                                                ].map((qty) => (
                                                    <div className={classes.qtyBox} key={qty.label}>
                                                        <div className={classes.qtyLabel}>{qty.label}</div>
                                                        <div className={classes.qtyValue}>{qty.value}</div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className={classes.itemCardBottom}>
                                                <div>
                                                    <div className={classes.fieldLabel}>Reason</div>
                                                    {canEditReason(item, snag, userDetails) ? (
                                                        <FormControl variant="outlined" size="small" fullWidth>
                                                            <Select
                                                                className={classes.reasonSelect}
                                                                value={item.reason || ''}
                                                                displayEmpty
                                                                disabled={reasonUpdatingId === `${snag._id}-${item.itemId}`}
                                                                onChange={(event) => handleReasonChange(snag, item, event.target.value)}
                                                            >
                                                                <MenuItem value="" disabled>
                                                                    Select reason
                                                                </MenuItem>
                                                                {REASON_OPTIONS.map((option) => (
                                                                    <MenuItem key={option} value={option}>
                                                                        {option}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : item.reason ? (
                                                        <span
                                                            className={classes.reasonChip}
                                                            style={getReasonChipStyle(item.reason)}
                                                        >
                                                            {item.reason}
                                                        </span>
                                                    ) : (
                                                        <span className={classes.remarkText}>—</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className={classes.fieldLabel}>Remarks</div>
                                                    <div className={classes.remarkText}>{item.remark || '—'}</div>
                                                </div>
                                                <div className={classes.rowActions}>
                                                    {showOutwardButton && (
                                                        <Button
                                                            className={`${classes.actionButton} ${classes.outwardBtn}`}
                                                            variant="contained"
                                                            disableElevation
                                                            fullWidth
                                                            disabled={isBusy}
                                                            onClick={() => openOutwardDialog(snag, item)}
                                                        >
                                                            Outward
                                                        </Button>
                                                    )}
                                                    {showChainButtons && (
                                                        <div className={classes.chainActions}>
                                                            <Button
                                                                className={`${classes.actionButton} ${classes.approveBtn}`}
                                                                variant="contained"
                                                                disableElevation
                                                                disabled={isBusy}
                                                                onClick={() => handleApproveNotPresentInBom(snag, item)}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                className={`${classes.actionButton} ${classes.rejectBtn}`}
                                                                variant="outlined"
                                                                disableElevation
                                                                disabled={isBusy}
                                                                onClick={() => handleRejectNotPresentInBom(snag, item)}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </div>
                                                    )}
                                                    {!canAct && !showOutwardButton && !showChainButtons && !isOutwardedItem(item) && (
                                                        <div className={classes.viewOnlyNote}>
                                                            View only · {snag.assignedTo || '—'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {statusDetails.length > 0 && (
                                                <div className={classes.statusDetailList}>
                                                    {statusDetails.map((detail) => (
                                                        <Chip
                                                            key={`${detail.label}-${detail.value}`}
                                                            size="small"
                                                            className={classes.detailChip}
                                                            label={`${detail.label}: ${detail.value}`}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {!(snag.items || []).length && (
                                    <Typography variant="body2" color="textSecondary">
                                        No items in this snag.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        );
                    })}
                    {!visibleSnags.length && (
                        <div className={classes.logEmpty}>
                            {isDetailsFiltered
                                ? 'No items match the selected filters for this project.'
                                : 'No snags found for this project.'}
                        </div>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px 20px', backgroundColor: '#f6f8fb' }}>
                    <Button
                        className={classes.closeBtn}
                        variant="outlined"
                        onClick={() => setDetailsOpen(false)}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={logDialogOpen}
                onClose={closeLogDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle} disableTypography>
                    <Typography className={classes.dialogTitleText}>
                        Snag {logTarget?.snagIndex != null ? logTarget.snagIndex + 1 : ''} — Activity Log
                    </Typography>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {logsLoading ? (
                        <div className={classes.logEmpty}>Loading history…</div>
                    ) : snagLogs.length ? (
                        <div className={classes.logTimeline}>
                            {snagLogs.map((log) => (
                                <div className={classes.logEntry} key={log._id}>
                                    <div className={classes.logEntryHeader}>
                                        <span className={classes.logAction}>
                                            {getLogActionLabel(log.action)}
                                        </span>
                                        <span className={classes.logMeta}>
                                            {formatDate(log.createdAt)}
                                        </span>
                                    </div>
                                    <div className={classes.logMeta} style={{ marginBottom: 4 }}>
                                        By {log.performedBy?.name || log.performedBy?.email || '—'}
                                    </div>
                                    <div className={classes.logDetailText}>
                                        {formatLogDetails(log)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={classes.logEmpty}>No activity recorded for this snag yet.</div>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px 20px', backgroundColor: '#f6f8fb' }}>
                    <Button
                        className={classes.closeBtn}
                        variant="outlined"
                        onClick={closeLogDialog}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={outwardDialogOpen}
                onClose={closeOutwardDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle} disableTypography>
                    <Typography className={classes.dialogTitleText}>Outward Details</Typography>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 8 }}>
                        <Typography variant="body2" style={{ color: '#5f6b7a' }}>
                            {outwardTarget?.item?.pieceName || '—'}
                            {' · Qty '}
                            {outwardTarget?.item?.qty ?? '—'}
                        </Typography>
                        <TextField
                            label="Mode of Transport"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            value={modeOfTransport}
                            onChange={(e) => setModeOfTransport(e.target.value)}
                            placeholder="e.g. By Hand, Courier, Truck"
                        />
                        <TextField
                            label="Dispatch Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: getMinDispatchDate() }}
                            value={dispatchDate}
                            onChange={(e) => setDispatchDate(e.target.value)}
                            helperText="Today or a future date only"
                        />
                    </Box>
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px 20px', backgroundColor: '#f6f8fb' }}>
                    <Button
                        className={classes.closeBtn}
                        variant="outlined"
                        onClick={closeOutwardDialog}
                        disabled={!!actionLoadingId}
                    >
                        Cancel
                    </Button>
                    <Button
                        className={classes.actionBtn}
                        variant="contained"
                        onClick={handleOutward}
                        disabled={
                            !!actionLoadingId
                            || !modeOfTransport.trim()
                            || !dispatchDate
                            || !isDispatchDateAllowed(dispatchDate)
                        }
                    >
                        Confirm Outward
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const mapStateToProps = (state) => ({
    userDetails: state.user.userDetails,
});

const mapDispatchToProps = (dispatch) => ({
    setLoading: (value) => dispatch(setLoading(value)),
    setSnackInfo: (msg, severity) => dispatch(setSnackInfo(msg, severity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HardwareSnags);
